import React from 'react';
import { Link } from 'react-router-dom';
import { IMAGES, TESTIMONIALS, TOUR_STOPS } from '../lib/content';
import { MapPin } from 'lucide-react';

const HOME_OFFERINGS = [
  {
    title: 'Dinner Date',
    duration: '4 hours · from $2,000',
    blurb: 'Shared plates, a bottle of wine, and the delicious anticipation of dessert.',
    img: IMAGES.gallery[6],
  },
  {
    title: 'Overnight',
    duration: '16 hours · from $7,000',
    blurb: 'The special intimacy of waking up slowly together, and breakfast in bed after thoroughly enjoying each other.',
    img: IMAGES.gallery[4],
  },
  {
    title: 'Fly Me To You',
    duration: 'From $5,000 + flights',
    blurb: 'Passport-ready. Pack me on your next business trip or adventure. Travel is my ultimate aphrodisiac.',
    img: IMAGES.gallery[7],
  },
];

export default function Home() {
  const touring = TOUR_STOPS.filter((s) => s.status !== 'home').slice(0, 3);

  return (
    <div>
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-28 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-6 zb-fade-up">
            <div className="font-label text-zb-accent mb-6" data-testid="hero-overline">
              Independent Companion · Melbourne
            </div>
            <h1 className="font-serif text-[52px] leading-[0.95] md:text-[88px] md:leading-[0.95] text-zb-ink tracking-tight">
              Are you craving <span className="italic text-zb-brand">something real?</span>
            </h1>
            <p className="mt-8 text-zb-ink2 text-lg md:text-xl font-light leading-relaxed max-w-xl">
              I&rsquo;m Zahra: playful, passionate, and genuinely down to earth.
            </p>
            <p className="mt-3 text-zb-ink2 text-lg md:text-xl font-light leading-relaxed max-w-xl">
              Exactly what you&rsquo;ve been missing.
            </p>
            <p className="mt-6 font-serif italic text-zb-ink text-xl md:text-2xl">
              I&rsquo;m so glad you found me.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <Link
                to="/contact"
                data-testid="hero-cta-book"
                className="rounded-full bg-zb-brand hover:bg-zb-brandHover text-zb-bg px-8 py-3.5 text-xs tracking-[0.22em] uppercase transition-colors"
              >
                Let&rsquo;s meet
              </Link>
              <Link
                to="/about"
                data-testid="hero-cta-about"
                className="text-zb-ink hover:text-zb-brand text-xs tracking-[0.22em] uppercase underline underline-offset-8 decoration-zb-border hover:decoration-zb-brand transition-colors"
              >
                More about me
              </Link>
            </div>
          </div>
          <div className="md:col-span-6 zb-fade-up zb-delay-2">
            <div className="zb-img-wrap aspect-[4/5] md:aspect-[3/4]">
              <img src={IMAGES.hero} alt="Zahra Belle" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT BRIDGE — image + supporting paragraph */}
      <section className="bg-zb-bg2 border-b border-zb-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20 grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="md:col-span-5 zb-fade-up order-2 md:order-1">
            <div className="zb-img-wrap aspect-[4/5]">
              <img src={IMAGES.about} alt="Zahra Belle" loading="lazy" />
            </div>
          </div>
          <div className="md:col-span-7 zb-fade-up zb-delay-1 order-1 md:order-2">
            <div className="font-label text-zb-accent mb-5">About me</div>
            <h2 className="font-serif text-4xl md:text-5xl text-zb-ink leading-[1.1]">
              Real chemistry. <span className="italic text-zb-brand">Real connection.</span>
            </h2>
            <p className="mt-8 text-zb-ink2 text-[16px] md:text-lg leading-relaxed max-w-xl">
              What makes time with me different is how real it feels. Nothing
              forced, performative, or transactional. Slow, hungry kisses and
              teasing tension. Laughter and conversation that becomes
              erotically charged, and intimacy that builds each time we meet.
            </p>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="inline-block mt-10 rounded-full border border-zb-border hover:border-zb-brand text-zb-ink hover:text-zb-brand px-7 py-3 text-xs tracking-[0.22em] uppercase transition-colors"
            >
              More about me →
            </Link>
          </div>
        </div>
      </section>

      {/* TOUR CALLOUT STRIP — lighter, placed before testimonials */}
      <section className="bg-zb-bg border-b border-zb-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-14 grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-3 flex items-center gap-3">
            <MapPin size={20} className="text-zb-brand" />
            <span className="font-label text-zb-accent text-sm tracking-widest">Currently touring</span>
          </div>
          <div className="md:col-span-7 flex flex-wrap gap-x-10 gap-y-3 text-lg md:text-2xl">
            {touring.map((s, i) => (
              <span key={i} className="font-serif">
                <span className="text-zb-ink">{s.city}</span>
                <span className="text-zb-ink2"> · {s.dates}</span>
              </span>
            ))}
          </div>
          <div className="md:col-span-2 md:text-right">
            <Link
              to="/tour"
              data-testid="home-tour-link"
              className="inline-block font-label text-zb-brand hover:text-zb-brandHover underline underline-offset-4 text-xs tracking-widest"
            >
              View tour →
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL — moved higher for social proof */}
      <section>
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-14 md:py-20 text-center">
          <div className="font-label text-zb-accent mb-6">Testimonials</div>
          <p className="font-serif text-2xl md:text-4xl text-zb-ink italic leading-snug">
            &ldquo;{TESTIMONIALS[0].quote}&rdquo;
          </p>
          <div className="mt-8 font-label text-zb-accent">
            {TESTIMONIALS[0].author}
            {TESTIMONIALS[0].date ? ` · ${TESTIMONIALS[0].date}` : ''}
          </div>
          <Link
            to="/testimonials"
            data-testid="home-testimonials-link"
            className="inline-block mt-10 font-label text-zb-brand hover:text-zb-brandHover underline underline-offset-8"
          >
            Read more →
          </Link>
        </div>
      </section>

      {/* WAYS TO MEET — smaller, tighter cards */}
      <section className="bg-zb-bg2 border-y border-zb-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="font-label text-zb-accent mb-3">Ways to meet</div>
              <h2 className="font-serif text-3xl md:text-4xl text-zb-ink leading-tight">
                Choose your <span className="italic text-zb-brand">indulgence.</span>
              </h2>
            </div>
            <Link
              to="/rates"
              data-testid="home-rates-link"
              className="font-label text-zb-brand hover:text-zb-brandHover underline underline-offset-8 text-[10.5px]"
            >
              See all rates →
            </Link>
          </div>
          {/* mobile: horizontal scroll · desktop: 3-col compact */}
          <div
            className="flex md:grid md:grid-cols-3 gap-4 md:gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-6 px-6 md:mx-0 md:px-0 pb-2 md:pb-0"
            data-testid="home-offerings"
          >
            {HOME_OFFERINGS.map((o, i) => (
              <Link
                to="/rates"
                key={o.title}
                data-testid={`home-offering-${i}`}
                className="group block flex-shrink-0 w-[78%] sm:w-[60%] md:w-auto snap-start zb-fade-up"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="zb-img-wrap aspect-[4/3]">
                  <img src={o.img} alt="" loading="lazy" />
                </div>
                <div className="mt-4">
                  <div className="font-serif text-xl md:text-2xl text-zb-ink group-hover:text-zb-brand transition-colors">
                    {o.title}
                  </div>
                  <div className="font-label text-zb-accent mt-1 text-[10px]">{o.duration}</div>
                  <p className="mt-2 text-zb-ink2 text-[13.5px] leading-relaxed">
                    {o.blurb}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MID-PAGE CTA */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-14 md:py-18 text-center">
        <div className="font-label text-zb-accent mb-5">Shall we?</div>
        <h2 className="font-serif text-4xl md:text-5xl text-zb-ink leading-tight">
          An <span className="italic text-zb-brand">invitation.</span>
        </h2>
        <p className="mt-5 text-zb-ink2 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
          Availability is limited. If you feel the pull, trust it.
        </p>
        <Link
          to="/contact"
          data-testid="home-mid-cta-book"
          className="inline-block mt-8 rounded-full bg-zb-brand hover:bg-zb-brandHover text-zb-bg px-10 py-4 text-xs tracking-[0.22em] uppercase transition-colors"
        >
          Make a date
        </Link>
      </section>

      {/* TESTIMONIAL — full image with compact quote card overlaid */}
      <section className="relative overflow-hidden my-0 h-[85vh] md:h-[92vh] max-h-[900px]">
        <img
          src={IMAGES.homeQuote}
          alt=""
          className="zb-quote-image absolute inset-0 w-full h-full object-cover"
        />
        {/* Soft vignette to keep the corner text legible without darkening the whole image */}
        <div
          className="absolute inset-0 pointer-events-none hidden md:block"
          style={{
            background:
              'linear-gradient(120deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.55) 100%)',
          }}
        />
        <div className="relative h-full max-w-7xl mx-auto px-4 md:px-12 flex items-end justify-center md:justify-end pb-6 md:pb-16">
          <figure
            className="max-w-[16rem] md:max-w-xs w-full backdrop-blur-sm border border-zb-bg/15 text-zb-bg p-4 md:p-6"
            style={{ backgroundColor: 'rgba(122, 42, 61, 0.72)' }}
          >
            <div className="font-label text-zb-bg/75 mb-2 md:mb-3 text-[9.5px] md:text-[10px] tracking-[0.28em]">
              In their words
            </div>
            <blockquote className="font-serif italic text-[15px] md:text-lg leading-[1.4]">
              &ldquo;Mesmerising kisser. You&rsquo;ll breathe slower while your
              heart races. Down to earth and an amazing ability to know what
              you want without saying a word.&rdquo;
            </blockquote>
            <figcaption className="mt-3 md:mt-4 pt-2.5 md:pt-3 border-t border-zb-bg/25 font-label text-zb-bg/75 text-[9.5px] md:text-[10px] tracking-[0.22em]">
              Joel &middot; April 2024
            </figcaption>
          </figure>
        </div>
      </section>

      {/* GALLERY PEEK */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="font-label text-zb-accent mb-4">Gallery</div>
            <h2 className="font-serif text-4xl md:text-6xl text-zb-ink">
              A glimpse, <span className="italic">better in the flesh.</span>
            </h2>
          </div>
          <Link
            to="/gallery"
            data-testid="home-gallery-link"
            className="font-label text-zb-brand hover:text-zb-brandHover underline underline-offset-8"
          >
            See the full gallery →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {IMAGES.gallery.slice(0, 4).map((src, i) => (
            <div
              key={i}
              className={`zb-img-wrap ${i % 2 === 0 ? 'aspect-[3/4]' : 'aspect-[4/5] md:mt-10'}`}
            >
              <img src={src} alt="" loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-22 text-center">
        <div className="font-label text-zb-accent mb-6">One last thing</div>
        <h2 className="font-serif text-4xl md:text-6xl text-zb-ink leading-tight">
          Say <span className="italic text-zb-brand">hello.</span>
        </h2>
        <p className="mt-6 text-zb-ink2 text-lg max-w-xl mx-auto">
          A thoughtful message is all it takes. I reply personally, usually
          within 24 hours.
        </p>
        <Link
          to="/contact"
          data-testid="home-bottom-cta-book"
          className="inline-block mt-10 rounded-full bg-zb-ink hover:bg-zb-brand text-zb-bg px-10 py-4 text-xs tracking-[0.22em] uppercase transition-colors"
        >
          Send an inquiry
        </Link>
      </section>
    </div>
  );
}
